import { normalizeCropCode } from "pages/admin/experiments/cropExtraction/cropCodes";
import { DbfRow, NamedDbfTable, findField, findTable, runDbfParser, toNumber, toString } from "pages/admin/experiments/cropExtraction/parsers/genericDbf";
import { CropRecord, ParserResult } from "pages/admin/experiments/cropExtraction/types";

// SchlaegeExport.dbf ships from the RLP portal with the columns confirmed
// against the real DE-RP sample: SCHLAGNR (field number), KTA_AJ (current-
// year crop code), KTA_VJ (previous-year fallback), SCHLAGFLAE (area in m²
// for cropland), LE_FLAECHE (area in m² for landscape elements).
const buildSchlaegeExportExtractor = (table: NamedDbfTable): ((row: DbfRow) => CropRecord | null) => {
  const cropCodeAj = findField(table.rows, ["KTA_AJ", "kta_aj"]);
  const cropCodeVj = findField(table.rows, ["KTA_VJ", "kta_vj"]);
  const fieldNumberCol = findField(table.rows, ["SCHLAGNR", "schlagnr"]);
  const schlagflaeCol = findField(table.rows, ["SCHLAGFLAE", "schlagflae"]);
  const leFlaecheCol = findField(table.rows, ["LE_FLAECHE", "le_flaeche"]);
  const ownerCol = findField(table.rows, ["BNR15", "bnr15"]);

  return (row) => {
    const codeAj = cropCodeAj ? normalizeCropCode(toString(row[cropCodeAj])) : "";
    const codeVj = cropCodeVj ? normalizeCropCode(toString(row[cropCodeVj])) : "";
    const fieldNumber = fieldNumberCol ? toString(row[fieldNumberCol]) : "";
    const owner = ownerCol ? toString(row[ownerCol]) : "";
    const schlagM2 = schlagflaeCol ? toNumber(row[schlagflaeCol]) : 0;
    const leM2 = leFlaecheCol ? toNumber(row[leFlaecheCol]) : 0;

    return {
      fieldId: owner && fieldNumber ? `${owner}/${fieldNumber}` : fieldNumber,
      fieldName: fieldNumber,
      cropCode: codeAj || codeVj,
      cropDescription: "",
      areaHa: (schlagM2 > 0 ? schlagM2 : leM2) / 10000,
      fieldNumber: fieldNumber || undefined,
    };
  };
};

// Legacy MFA + ERFFL schema (no real sample on hand). Kept for compatibility
// with the original spec — column names are best-effort heuristics.
const buildMfaErfflExtractor = (table: NamedDbfTable): ((row: DbfRow) => CropRecord | null) => {
  const cropCol = findField(table.rows, ["SNAR_CODE", "snar_code", "nutz_code"]);
  const cropDescCol = findField(table.rows, ["SNAR", "snar", "nutz_text"]);
  const fieldNameCol = findField(table.rows, ["bez", "schlagbez", "lage_bez", "schlag_bez", "name"]);
  const fieldIdCol = findField(table.rows, ["geo_id", "schlagnr", "schlag_id", "fid"]);
  const areaCol = findField(table.rows, ["flaeche_ha", "flaeche", "beantr_gro", "groesse"]);

  return (row) => {
    const fieldId = fieldIdCol ? toString(row[fieldIdCol]) : "";
    return {
      fieldId,
      fieldName: (fieldNameCol && toString(row[fieldNameCol])) || fieldId,
      cropCode: normalizeCropCode(cropCol ? toString(row[cropCol]) : ""),
      cropDescription: cropDescCol ? toString(row[cropDescCol]) : "",
      areaHa: areaCol ? toNumber(row[areaCol]) : 0,
      fieldNumber: fieldId || undefined,
    };
  };
};

export const parseElanRlp = (zipBuffer: ArrayBuffer): Promise<ParserResult> =>
  runDbfParser(zipBuffer, {
    notFoundMessage: "ELAN-RLP DBF not found or empty",
    pickTable: (tables) =>
      findTable(tables, (b) => b === "schlaegeexport") ??
      findTable(tables, (b) => b.includes("erffl")) ??
      findTable(tables, (b) => b.includes("mfa")) ??
      tables[0],
    buildExtractor: (table) => {
      // Branch on the picked table's basename — `SchlaegeExport.dbf` is the
      // farmer-facing RLP export, while MFA/ERFFL is the legacy schema.
      if (table.basename.toLowerCase() === "schlaegeexport") {
        return buildSchlaegeExportExtractor(table);
      }
      return buildMfaErfflExtractor(table);
    },
  });
